import Bugsnag from '@bugsnag/js'
import { useMutation } from 'react-query'
import { React, useState, useRef, useEffect } from 'react'
import { Box, Icon, InputAdornment, Typography, CardMedia, CircularProgress } from '@mui/material'
import ACDDrawer from './ACDDrawer'
import AdxInput from 'components/custom/AdxInput'
import defaultImage from 'assets/images/default_avatar.png'
import AdxPagination from 'components/custom/AdxPagination'
import PerfSortTable from 'components/reusable/table/PerfSortTable'
import {
  getAllParticipantData,
  getAllParticipants,
} from 'services/api/modules/performance/appraisal/participants'
import AdxButton from 'components/custom/AdxButton'
import { downloadResponseFile } from 'services/helper/fileDownload'

const ACDSortingTable = ({ status_name, appraisalId, cycleName }) => {
  const inputEl = useRef('')
  const [currentPage, setCurrentPage] = useState(1)
  const [searchTerm, setSearchTerm] = useState('')

  const {
    isLoading,
    data,
    mutate: getParticipants,
  } = useMutation((values) => getAllParticipants(appraisalId, currentPage, values), {})

  useEffect(() => {
    let newSearch = {}
    newSearch = {
      search: searchTerm,
    }
    setTimeout(() => {
      getParticipants(newSearch)
    }, 1000)
  }, [currentPage, searchTerm])

  const participants = data?.data?.data

  const employeeColumn = [
    {
      Header: 'Employee',
      accessor: 'name',
      Cell: (props) => {
        const { value } = props
        return (
          <Box display="flex" alignItems="center" justifyContent="flex-start">
            <Box sx={{ position: 'relative' }}>
              <CardMedia
                component="img"
                sx={{
                  height: 40,
                  width: 40,
                  objectFit: 'cover',
                  borderRadius: '50%',
                  marginRight: 2,
                }}
                src={props?.row?.original?.profile_image || defaultImage}
              />
            </Box>
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="space-around"
              alignItems="flex-start"
            >
              <Typography>{value}</Typography>
              <Typography variant="body2" sx={{ color: 'light-gray-text.main' }}>
                {props?.row?.original?.designation}
              </Typography>
            </Box>
          </Box>
        )
      },
    },
    {
      Header: 'Department',
      accessor: 'department_name',
    },
    {
      Header: 'Overall score',
      accessor: 'total_score', // sorting is based on accessor value, if string then A-Z. If number small - large
      Cell: (props) => {
        const { value } = props

        return (
          <Box>
            <Typography>{value ? Math.round(value * 50) / 10 : '--'}</Typography>
          </Box>
        )
      },
    },
  ]
  const getSearchTerm = (e) => {
    setSearchTerm(e.target.value)
  }
  // Drawer function
  const [staff, setStaff] = useState([])
  const [drawerState, setDrawerState] = useState({ right: false })
  const handleDrawer = (anchor, open) => (item) => {
    setDrawerState({ ...drawerState, [anchor]: open })
    setStaff(item)
  }
  const { mutate: downloadAppraisalResult } = useMutation(getAllParticipantData, {
    onSuccess: (res) => {
      downloadResponseFile(res, {
        fileName: `${cycleName}_appraisal_result.xlsx`,
        contentType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        successMessage: 'Appraisal result downloaded.',
      })
    },
    onError: (error) => {
      if (error.status === 422) {
        console.log(error)
      } else {
        Bugsnag.notify(error.data)
      }
    },
  })
  const handleDownloadResult = () => {
    downloadAppraisalResult(appraisalId)
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
      }}
    >
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: { xs: null, sm: 'center' },
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', sm: 'row' },
          rowGap: 1,
          p: 2,
          borderBottom: 1,
          borderColor: 'gray-line.main',
        }}
      >
        <AdxInput
          sx={{
            width: { xs: 1, sm: 4 / 6 },
          }}
          value={searchTerm}
          ref={inputEl}
          onChange={getSearchTerm}
          startAdornment={
            <InputAdornment position="start">
              <Icon>search</Icon>
            </InputAdornment>
          }
          placeholder="Search employee name"
        />

        {status_name !== 'Completed' ? null : (
          <AdxButton
            variant={'outlined'}
            onClick={handleDownloadResult}
            sx={{ width: 'calc(100% * 1 / 5)' }}
          >
            Download Result
          </AdxButton>
        )}
      </Box>

      {isLoading && (
        <Box height="30vh" display="flex" justifyContent="center" alignItems="center">
          <CircularProgress color="alternate" size={60} />
        </Box>
      )}
      {!isLoading && (
        <Box sx={{ width: '100%' }}>
          <PerfSortTable
            column={employeeColumn}
            data={participants ?? []}
            nav="View"
            onClick={handleDrawer('right', true)}
            sxNav={{ textDecoration: 'underline' }}
            sxCell
            sx={{ boxShadow: 'none' }}
            deleteUser={true}
            fromEvaluation={true}
            getParticipants={getParticipants}
            height={'calc(100vh - 380px)'}
          />
          {data?.data?.meta?.last_page > 1 ? (
            <Box
              width="100%"
              display="flex"
              justifyContent="center"
              py={1}
              backgroundColor="white.main"
            >
              <AdxPagination
                count={data?.data?.meta?.last_page}
                onChange={(event, page) => setCurrentPage(page)}
                defaultPage={currentPage}
              />
            </Box>
          ) : null}
        </Box>
      )}
      <ACDDrawer
        drawerState={drawerState}
        handleDrawer={handleDrawer}
        staff={staff}
        appraisalId={appraisalId}
      />
    </Box>
  )
}

export default ACDSortingTable
